import type { FC, PropsWithChildren } from 'react'
import { useMemo } from 'react'

import { createStore, initialState, Provider } from '@liveHelper/models/store'
import type { LiveHelperParams } from '@liveHelper/params'
import { configs } from '@liveHelper/params'

import { getSentryConfig } from '@core/logging'
import { resolveParams } from '@core/params'
import { getPageUrl } from '@core/window'

/** Deep-link / embed: `fw_oto_action` on the page URL forces bypass of widget_availability hidden (e.g. never). */
function shouldBypassWidgetAvailabilityFromPageUrl(): boolean {
  try {
    const pageUrl = getPageUrl()
    const action = pageUrl
      ? new URL(pageUrl).searchParams.get('fw_oto_action')
      : null
    return action === 'show' || action === 'call' || action === 'chat'
  } catch {
    return false
  }
}

interface Props {
  widgetAttributes: Record<string, string>
  initialParams: LiveHelperParams
}

/** Provider component that provides the one-to-one store. */
export const OneToOneStoreProvider: FC<PropsWithChildren<Props>> = ({
  widgetAttributes,
  initialParams,
  children,
}) => {
  const store = useMemo(() => {
    const name = widgetAttributes.name ?? initialParams.widget_id ?? ''
    const resolvedParams = resolveParams(
      {
        defaultParams: { ...initialParams, ...initialState.params },
        widgetAttributes,
      },
      configs,
    )

    const params = shouldBypassWidgetAvailabilityFromPageUrl()
      ? { ...resolvedParams, bypass_widget_availability_hidden: true }
      : resolvedParams

    // This is for adding chat channel id from anywhere else other than widget attributes
    const sentryConfig = getSentryConfig()
    if (
      !sentryConfig.tags.map(([name]) => name).includes('fwn_chat_channel_id')
    ) {
      sentryConfig.addTag(
        'fwn_chat_channel_id',
        params.chat_channel_id ?? 'undefined',
      )
    }

    return createStore(name, () => ({
      ...initialState,
      params,
    }))
  }, [initialParams, widgetAttributes])

  return <Provider store={store}>{children}</Provider>
}
